import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Settings, Moon, Flame, Sparkle } from "lucide-react";
import SettingsDrawer from "@/components/SettingsDrawer";

const TOP_LOGO_TESTID = "app-shell-logo";
const SETTINGS_TESTID = "app-shell-settings-button";

/**
 * Persistent top bar: Continua wordmark left, Settings cog right.
 * Tapping the cog opens a right-side slide-over drawer (NOT a route change).
 * Intentionally minimal — no breadcrumb, no title, no actions.
 */
export const TopBar = ({ onOpenSettings }) => (
  <header
    className="relative z-20 flex items-center justify-between px-6 sm:px-8 py-5"
    style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1.25rem)" }}
    data-testid="app-shell-top-bar"
  >
    <Link
      to="/app"
      data-testid={TOP_LOGO_TESTID}
      className="flex items-center gap-2 font-serif-display text-xl tracking-tight text-violet-50 hover:text-white transition-colors"
    >
      <img
        src="/continua-logo-small.png"
        alt=""
        aria-hidden="true"
        className="w-7 h-7 object-contain"
        data-testid="app-shell-logo-symbol"
      />
      Continua
    </Link>
    <button
      type="button"
      onClick={onOpenSettings}
      data-testid={SETTINGS_TESTID}
      aria-label="Open settings"
      className="p-2 -m-2 text-violet-200/70 hover:text-violet-50 transition-colors"
    >
      <Settings className="w-5 h-5" strokeWidth={1.5} />
    </button>
  </header>
);

const NAV = [
  { key: "sessions", label: "Sessions", to: "/rest", Icon: Moon },
  { key: "home", label: "Home", to: "/app", Icon: Flame },
  { key: "coming-soon", label: "Coming soon", to: "/coming-soon", Icon: Sparkle },
];

/**
 * Persistent bottom navigation. Three tabs — Sessions / Home / Coming Soon.
 * Selected tab carries a soft silver pill behind the icon.
 */
export const BottomNav = ({ bottom = "0px" }) => {
  const loc = useLocation();
  const isActive = (to) => {
    if (to === "/app") return loc.pathname === "/app" || loc.pathname === "/";
    if (to === "/rest") return ["/rest", "/adventure", "/morning"].includes(loc.pathname);
    return loc.pathname === to;
  };
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30 flex justify-center pointer-events-none"
      style={{ bottom: `calc(${bottom} + env(safe-area-inset-bottom, 0px))` }}
      data-testid="app-shell-bottom-nav"
    >
      <div className="mb-6 pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full glass-strong">
        {NAV.map(({ key, label, to, Icon }) => {
          const active = isActive(to);
          return (
            <Link
              key={key}
              to={to}
              aria-label={label}
              data-testid={`bottom-nav-${key}`}
              className={`relative flex items-center justify-center w-12 h-12 rounded-full transition-colors ${
                active ? "text-violet-50" : "text-violet-300/55 hover:text-violet-100"
              }`}
            >
              {active && (
                <span
                  aria-hidden="true"
                  className="absolute inset-1 rounded-full bg-violet-200/12 border border-violet-200/25"
                />
              )}
              <Icon className="relative w-5 h-5" strokeWidth={1.5} />
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

/**
 * Layout wrapper used by Home/Characters/Companion/Rest/Adventure/Morning.
 * Provides starfield + top bar + (optional) bottom nav + content padding.
 * The Settings cog opens a right-side slide-over drawer instead of routing.
 */
// Horizontal swipe order between the three main tabs. Only the UI content
// slides — the fixed StarField background never moves.
const SWIPE_ORDER = ["/rest", "/app", "/coming-soon"];
const swipeIndexFor = (path) => (path === "/adventure" ? 0 : SWIPE_ORDER.indexOf(path));

export default function AppShell({ children, withBottomNav = true, bottomNavBottom = "0px" }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const mainRef = useRef(null);
  const drag = useRef({ active: false, decided: false, horizontal: false, startX: 0, startY: 0, width: 0 });

  // Entrance: after a completed swipe, the incoming page slides in from the
  // travel side (transform-only, `both` fill → never fades, so the Play glow
  // stays solid and the content always ends visible). Computed synchronously.
  const [slideClass] = useState(() => {
    const dir = sessionStorage.getItem("continua-swipe-dir");
    if (dir) sessionStorage.removeItem("continua-swipe-dir");
    return dir === "next" ? "continua-slide-next" : dir === "prev" ? "continua-slide-prev" : "";
  });

  const neighbors = () => {
    const idx = swipeIndexFor(loc.pathname);
    return { idx, hasNext: idx >= 0 && idx + 1 < SWIPE_ORDER.length, hasPrev: idx >= 0 && idx - 1 >= 0 };
  };

  // iOS-style paging: the page LITERALLY follows the finger during the drag,
  // then either completes the transition (past threshold) or springs back.
  // Only this UI layer moves via transform — the fixed StarField never moves.
  const onPointerDown = (e) => {
    if (e.target.closest('input, button, a, textarea, [role="slider"], [data-no-swipe]')) { drag.current.active = false; return; }
    if (swipeIndexFor(loc.pathname) < 0) return; // only the three main tabs page
    drag.current = { active: true, decided: false, horizontal: false, startX: e.clientX, startY: e.clientY, width: window.innerWidth || 1 };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.decided) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      d.decided = true;
      d.horizontal = Math.abs(dx) > Math.abs(dy);
      if (d.horizontal && e.currentTarget.setPointerCapture) {
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
    }
    if (!d.horizontal) return;
    const { hasNext, hasPrev } = neighbors();
    let move = dx;
    // Rubber-band when there is no page in that direction.
    if ((move < 0 && !hasNext) || (move > 0 && !hasPrev)) move = move * 0.28;
    if (mainRef.current) {
      mainRef.current.style.transition = "none";
      mainRef.current.style.transform = `translateX(${move}px)`;
    }
  };

  const finishDrag = (e) => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    const el = mainRef.current;
    if (!d.horizontal) return;
    const dx = (e.clientX || d.startX) - d.startX;
    const width = d.width || window.innerWidth || 1;
    const threshold = Math.min(140, width * 0.22);
    const { idx, hasNext, hasPrev } = neighbors();
    const goNext = dx < -threshold && hasNext;
    const goPrev = dx > threshold && hasPrev;
    if (el) el.style.transition = "transform 300ms cubic-bezier(0.22,0.61,0.36,1)";
    if (goNext || goPrev) {
      if (el) el.style.transform = `translateX(${goNext ? -width : width}px)`;
      sessionStorage.setItem("continua-swipe-dir", goNext ? "next" : "prev");
      const target = SWIPE_ORDER[goNext ? idx + 1 : idx - 1];
      window.setTimeout(() => nav(target), 220);
    } else if (el) {
      el.style.transform = "translateX(0px)"; // spring back — under threshold
    }
  };

  return (
    <div
      className="relative min-h-screen text-violet-50"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      style={{ touchAction: "pan-y" }}
    >
      <TopBar onOpenSettings={() => setDrawerOpen(true)} />
      <main
        ref={mainRef}
        className={`relative z-10 ${slideClass}`}
        style={{ paddingBottom: "calc(8rem + env(safe-area-inset-bottom, 0px))" }}
      >{children}</main>
      {withBottomNav && <BottomNav bottom={bottomNavBottom} />}
      <SettingsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}
